import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
} from "@material-ui/core";
import intl from "react-intl-universal";

import { IWithballData } from "apis";
import { ICoordinate } from "types";
import { buildPlayerNameWithJerseyNumber } from "utils";

import { EditWithballDataDialog } from "../EditWithballDataDialog";

import { HoverLayer } from "./HoverLayer";
import { ITeamPlayer, WithballDataListProps } from "./types";
import { ListItem } from "./ListItem";

export const WithballDataList = ({
  data,
  homeTeam,
  awayTeam,
  onDataDelete,
}: WithballDataListProps) => {
  const [selectedData, setSelectedData] = useState<IWithballData | null>(null);
  const [editingData, setEditingData] = useState<IWithballData | null>(null);
  const [hoverLayerPosition, setHoverLayerPosition] = useState<ICoordinate>({
    x: 0,
    y: 0,
  });
  const [dataList, setDataList] = useState<IWithballData[]>([]);

  useEffect(() => {
    setDataList(data);
  }, [data]);

  const playerRecord = useMemo(() => {
    let record: Record<number, ITeamPlayer> = {
      [-1]: {
        name: "-",
      },
    };
    const homeTeamUsers = homeTeam ? homeTeam.users || [] : [];
    homeTeamUsers.map((user) => {
      return (record[user.id] = {
        name: buildPlayerNameWithJerseyNumber(user.name, user.number),
      });
    });
    const awayTeamUesrs = awayTeam ? awayTeam.users || [] : [];
    awayTeamUesrs.map((user) => {
      return (record[user.id] = {
        name: buildPlayerNameWithJerseyNumber(user.name, user.number),
      });
    });
    return record;
  }, [homeTeam, awayTeam]);

  const handleMouseEnter = useCallback(
    (data: IWithballData, pos: ICoordinate) => {
      setSelectedData(data);
      setHoverLayerPosition(pos);
    },
    [setSelectedData, setHoverLayerPosition]
  );

  const handleMouseLeave = useCallback(() => {
    setSelectedData(null);
  }, [setSelectedData]);

  const handleEdit = useCallback(
    (data: IWithballData) => {
      setEditingData(data);
    },
    [setEditingData]
  );

  const handleDialogClose = () => {
    setEditingData(null);
  };

  const handleDataUpdated = (updatedData: IWithballData) => {
    if (!editingData) {
      return;
    }

    const newDataList = [...dataList];
    const editingDataIndex = newDataList.findIndex(
      (each) => each.id === editingData.id
    );
    if (editingDataIndex < 0) {
      return;
    }

    newDataList.splice(editingDataIndex, 1, updatedData);
    setDataList(newDataList);

    // hide editing dialog
    setEditingData(null);
  };

  return (
    <>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{intl.get("STAT_TABLE_HEADER_TEAM")}</TableCell>
              <TableCell>{intl.get("STAT_TABLE_HEADER_ACTION")}</TableCell>
              <TableCell>{intl.get("STAT_TABLE_HEADER_FROM")}</TableCell>
              <TableCell>{intl.get("STAT_TABLE_HEADER_TO")}</TableCell>
              <TableCell>{intl.get("STAT_TABLE_HEADER_POSSESSION")}</TableCell>
              <TableCell>{intl.get("STAT_TABLE_HEADER_RESULT")}</TableCell>
              <TableCell>{intl.get("STAT_TABLE_HEADER_POSITOIN")}</TableCell>
              <TableCell>{intl.get("STAT_TABLE_HEADER_TIMESTAMP")}</TableCell>
              <TableCell>{intl.get("STAT_TABLE_HEADER_OPERATION")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {dataList.map((each) => {
              return (
                <ListItem
                  key={each.id}
                  data={each}
                  playerRecord={playerRecord}
                  onMouseLeave={handleMouseLeave}
                  onMouseEnter={handleMouseEnter}
                  onDelete={onDataDelete}
                  onEdit={handleEdit}
                  isHomeTeam={each.teamid === homeTeam?.id}
                />
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {selectedData && (
        <HoverLayer position={hoverLayerPosition} data={[selectedData]} />
      )}
      <EditWithballDataDialog
        data={editingData}
        onClose={handleDialogClose}
        homeTeam={homeTeam}
        awayTeam={awayTeam}
        onDataUpdated={handleDataUpdated}
      />
    </>
  );
};
